import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faBell } from "@fortawesome/free-solid-svg-icons";
import { useAuth0 } from "@auth0/auth0-react";
import { useNotificationPreferences } from "../../hooks/notifications/useNotificationPreferences";
import NotificationPreferences from "../../components/notifications/NotificationPreferences";
import PageHeader from "../../components/common/PageHeader";
import Loading from "../../components/common/Loading";

/**
 * NotificationPreferencesPage Component
 *
 * Full-page view for managing notification preferences and settings
 */
const NotificationPreferencesPage = () => {
  const { isAuthenticated } = useAuth0();
  const { data: preferences, isLoading, error } = useNotificationPreferences();

  // Handle preference save
  const handlePreferenceSave = () => {
    // Preferences are auto-saved by the NotificationPreferences component
    // This callback can be used for additional actions like showing success messages
  };

  if (!isAuthenticated) {
    return <Loading />;
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="notification-preferences-page min-h-screen bg-gray-50">
      {/* Page Header */}
      <PageHeader
        title="Notification Preferences"
        subtitle="Customize which notifications you receive"
        icon={faBell}
      >
        <div className="flex items-center">
          {/* Back to notifications */}
          <Link
            to="/notifications"
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
            Back to Notifications
          </Link>
        </div>
      </PageHeader>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error State - React Query handles errors automatically */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">
              Failed to load preferences: {error.message}
            </p>
          </div>
        )}

        {/* Preferences Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg mr-4">
                <FontAwesomeIcon icon={faBell} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Notification Settings
                </h2>
                <p className="text-sm text-gray-600">
                  Choose which notifications you want to receive
                </p>
              </div>
            </div>

            {/* Preferences Component */}
            <NotificationPreferences
              preferences={
                preferences?.preferences || {
                  follow: true,
                  "message.liked": true,
                  "message.replied": true,
                }
              }
              onSave={handlePreferenceSave}
              autoSave={true}
              showSaveButton={false}
            />
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">
            About Notifications
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Changes are saved automatically</li>
            <li>• You can update these preferences at any time</li>
            <li>• Thread notifications help you stay engaged in discussions</li>
            <li>• Some notifications may be required for security purposes</li>
          </ul>
        </div>

        {/* Navigation Links */}
        <div className="mt-6 flex justify-between">
          <Link
            to="/notifications"
            className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            ← View All Notifications
          </Link>
          <Link
            to="/profile"
            className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            Profile Settings →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferencesPage;
